import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Download, ListPlus, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { deleteScan, scansCsvUrl } from "../../../lib/siteScan";
import { Btn } from "../../ui";
import { QueueBar, useQueuePicks } from "../../telephony/queue";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { AutopilotPanel } from "../scan-leads/AutopilotPanel";
import { ExclusionSheet } from "../scan-leads/exclusions/ExclusionSheet";
import { useScanAutopilot } from "../scan-leads/useScanAutopilot";
import { ScanDetail } from "./ScanDetail";
import { ScanForm } from "./ScanForm";
import { SCAN_EXCLUDED_KEY, useSiteScan } from "./useSiteScan";
import { BulkScanDialog, ScanFilters, ScansTable, useScansTable } from "./scans-table";
import type { T } from "./types";

/** "Open the list": the way from a refused site to the list that refused it. */
function ExclusionsLink({ onOpen, t }: { onOpen: () => void; t: T }) {
  return (
    <Button variant="link" size="sm" className="h-auto shrink-0 p-0 text-[13px]" onClick={onOpen} aria-haspopup="dialog">
      {t("leads.exclusions.open")}
    </Button>
  );
}

/**
 * Site scanner: point it at a company's website and it says what the site is
 * built with, how far behind that is, how well it is looked after, and how to
 * reach the people who own it. The Skannid tab of Kliendiotsing.
 *
 * The tab is two halves. On top, the prompt: one field, one address, the full
 * verdict underneath it. Below, everything ever scanned as a filterable table -
 * because the second question after "what is this site" is always "which of the
 * three hundred we have looked at are worth calling this week".
 *
 * The bottom half was redesigned first and the top half was left behind, so for
 * a while this page carried both eras at once: pill rows and a `crm-card` under
 * a small hand-written heading, bordered panels and shared-kit buttons. Both
 * halves now draw from the same vocabulary.
 *
 * `?url=<domain>` opens that scan straight away. It is how the rest of the CRM
 * points at this page - a juhuklient the scanner found carries a link back to
 * the report it came from - and it keeps the address bar honest: open a row and
 * the URL says which one, so the page can be shared and reloaded.
 *
 * The scan autopilot sits between the verdict and the table as a one-line
 * strip (`prospecting/engines/EngineLine.tsx`), because every site it checks
 * becomes a row of this table; its numbers, settings and run log are behind
 * the strip's gear. While a batch runs, its poll pulls this table in with it,
 * so rows appear as the run writes them. The do-not-scan list is a button on
 * the table's heading row that opens it in a side panel, because it overrules
 * the autopilot and the pasted list the bulk button starts. Both were full
 * cards here after plan step 3 took them off the call queue; plan step 5 made
 * them a strip and a button, so the tab opens on the prompt and the list.
 *
 * A REFUSED SITE POINTS AT THE LIST THAT REFUSED IT. A scan the server turns
 * down because the owner opted out, and a bulk scan that left sites out for
 * the same reason, each say so with a link that opens the do-not-scan panel.
 * That is the question the operator has at that moment ("which list, and who
 * put it there"), and the panel's open state lives here for that reason.
 *
 * No head of its own: the page's head carries the title and this tab's
 * sentence. The bulk scan that sat in this tab's head is on the scans list's
 * heading row, because what it starts is more rows of that list.
 */
export function SiteScannerSection({ user }: { user: StaffUser }) {
  const t = useT();
  const { state, stage, recent, run } = useSiteScan();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exclusionsOpen, setExclusionsOpen] = useState(false);
  /** A one-line answer over the table; `excluded` adds the way to the list
   *  that left some of a bulk scan's sites out. */
  const [notice, setNotice] = useState<{ text: string; excluded?: boolean } | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [search, setSearch] = useSearchParams();
  const table = useScansTable();
  // "Which of the three hundred we have looked at are worth calling this week"
  // is the question this table was built for; the selection is how the answer
  // becomes a run rather than a list of numbers to copy out one at a time.
  const picks = useQueuePicks();
  const canManage = userCanPlatform(user, "manage:ariregister");
  // The engine that fills the table below; its busy poll reloads the table.
  const autopilot = useScanAutopilot(table.refresh);

  const scan = state.kind === "done" ? state.scan : null;

  /**
   * The domain the address bar last asked for, so writing the parameter after a
   * row click does not read back as a second request to scan the same site.
   */
  const asked = useRef<string | null>(null);

  const show = useCallback(
    (domain: string, refresh: boolean) => {
      asked.current = domain;
      setSearch(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("url", domain);
          return next;
        },
        { replace: true },
      );
      void run(domain, refresh);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [run, setSearch],
  );

  /** A row click loads that domain's stored scan into the detail view above. */
  const openScan = useCallback((domain: string) => show(domain, false), [show]);

  /** The same view, but fetched from the site again rather than read out of the
   *  store. The row menu is where a stale row gets asked about. */
  const rescan = useCallback((domain: string) => show(domain, true), [show]);

  // A domain in the URL is an instruction to open it. Runs on arrival and on
  // every later change to the parameter (the back button, a second link), but
  // never for a value this page wrote itself.
  const deepLink = search.get("url")?.trim() ?? "";
  useEffect(() => {
    if (!deepLink || asked.current === deepLink) return;
    asked.current = deepLink;
    void run(deepLink);
  }, [deepLink, run]);

  // Confirmed in a CRM dialog rather than `window.confirm`. The browser's box
  // is the one piece of chrome an operator cannot tell apart from a page they
  // did not mean to be on, and it was the only destructive question in the CRM
  // still being asked that way.
  const removeScan = useCallback(
    async (domain: string) => {
      try {
        await deleteScan(domain);
        table.refresh();
      } catch {
        setNotice({ text: t("scanner.table.deleteFailed") });
      } finally {
        setConfirmDel(null);
      }
    },
    [t, table],
  );

  return (
    // One open menu at a time - and, until now, no open menu at all: `Pop` reads
    // its open slot from this context, so with no host on the page every click
    // on a scan row's three-dot button silently did nothing.
    <PopHost>
      <div className="space-y-5">
        <ScanForm
          compact={Boolean(scan) || state.kind === "error"}
          busy={state.kind === "busy"}
          busyTarget={state.kind === "busy" ? state.target : ""}
          stage={stage}
          recent={recent}
          onScan={(url) => show(url, false)}
          t={t}
        />

        {/* The CRM's warning colour rather than a hand-picked amber: a scan that
            could not finish is the same kind of news as a waiting status, and it
            should not be a shade of its own. */}
        {state.kind === "error" && (
          <p className="crm-alert flex-wrap">
            <AlertTriangle className="size-4 shrink-0" />
            <span className="min-w-0 flex-1">{t(state.messageKey)}</span>
            {state.messageKey === SCAN_EXCLUDED_KEY ? (
              <ExclusionsLink onOpen={() => setExclusionsOpen(true)} t={t} />
            ) : null}
          </p>
        )}

        {notice && (
          <p className="crm-card flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-sm text-muted-foreground" role="status">
            <span className="min-w-0 flex-1">{notice.text}</span>
            {notice.excluded ? <ExclusionsLink onOpen={() => setExclusionsOpen(true)} t={t} /> : null}
          </p>
        )}

        {state.kind === "busy" && !scan && (
          <div className="flex justify-center py-10 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        )}

        {scan && (
          <ScanDetail
            scan={scan}
            refreshing={state.kind === "busy"}
            onRefresh={() => rescan(scan.domain)}
            t={t}
          />
        )}

        {/* The engine, above the table it fills. The strip carries its own
            bottom margin, which collapses into this column's gap. */}
        <AutopilotPanel
          state={autopilot.state}
          onChanged={() => {
            void autopilot.refresh();
            table.refresh();
          }}
          canManage={canManage}
          t={t}
        />

        {/* ── Everything ever scanned ────────────────────────────────────── */}
        {/* Not wrapped in a card: `ScansTable` draws its own, and a card inside
            a card is two shadows saying one thing. This is the section's own
            head and toolbar, the shape every list page in the CRM uses. */}
        <section className="space-y-3 pt-2">
          {/* The list's heading, and on its right what decides which sites get
              INTO the list: the do-not-scan list and the bulk scan. Refresh
              and export act on the current filters, so they ride at the end of
              the filter bar below; these two do not care what is filtered, and
              the filter bar already fills a laptop's line on its own. */}
          <div className="flex flex-wrap items-center gap-2.5 px-1.5">
            <h2 className="crm-card-t">{t("scanner.table.title")}</h2>
            <span className="crm-push flex flex-wrap gap-2.5">
              <ExclusionSheet
                open={exclusionsOpen}
                onOpenChange={setExclusionsOpen}
                canManage={canManage}
                t={t}
              />
              {canManage ? (
                <Btn small onClick={() => setBulkOpen(true)}>
                  <ListPlus className="size-4" /> {t("scanner.bulk.open")}
                </Btn>
              ) : null}
            </span>
          </div>

          <ScanFilters
            filters={table.filters}
            patch={table.patchFilters}
            reset={table.reset}
            qInput={table.qInput}
            setQInput={table.setQInput}
            facets={table.facets}
            total={table.total}
            actions={
              <>
                <Btn small onClick={table.refresh} disabled={table.loading}>
                  <RefreshCw className={table.loading ? "size-4 animate-spin" : "size-4"} />
                  {t("common.refresh")}
                </Btn>
                <a className="crm-btn crm-btn-sm" href={scansCsvUrl(table.params)} download>
                  <Download className="size-4" /> {t("scanner.table.export")}
                </a>
              </>
            }
            t={t}
          />

          <QueueBar picks={picks} />

          <ScansTable
            items={table.items}
            loading={table.loading}
            total={table.total}
            page={table.page}
            pageSize={table.pageSize}
            sort={table.sort}
            order={table.order}
            onSort={table.toggleSort}
            onPage={table.setPage}
            onOpen={openScan}
            onRescan={rescan}
            onDelete={setConfirmDel}
            canManage={canManage}
            picks={picks}
            t={t}
          />
        </section>
      </div>

      <BulkScanDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onStarted={({ accepted, excluded }) => {
          const skipped = excluded?.length ?? 0;
          setNotice(
            skipped
              ? {
                  text: `${t("scanner.bulk.started", { count: accepted })} ${t("scanner.bulk.skippedExcluded", { n: skipped })}`,
                  excluded: true,
                }
              : { text: t("scanner.bulk.started", { count: accepted }) },
          );
          // The batch runs in the background; a delayed refresh catches the
          // first few rows without polling.
          setTimeout(() => table.refresh(), 4000);
        }}
        t={t}
      />

      {confirmDel ? (
        <ConfirmModal
          title={t("scanner.table.delete")}
          body={t("scanner.table.deleteConfirm", { domain: confirmDel })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => removeScan(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </PopHost>
  );
}
